import globalStorePlugin from '~~/common/vue/store/global';
import userStorePlugin from '~~/common/vue/store/user';
import searchStorePlugin from '~~/search/common/vue/store/search';
import notificationsStorePlugin from '~~/common/vue/components/notifications/store';
import {
  PAGINATION_DYNAMIC,
  PAGINATION_STATIC,
} from '~/constants';
import {
  injectResultsAfterResultTypes,
  createIsResultOfType,
  createIsResultOfOneOfTypes,
  splitProductAdsForABTest,
} from '~/utils';
import { trackPageLoad, trackAdClick } from '~/trackers/dwell-time';
import {
  privacyProxyMs,
  shoppingBingExperiment,
  webProductAdsFullwidthExperiment,
} from '~~/common/js/constants/feature-flags';

const isProductAds = createIsResultOfType('productAds');
const isAd = createIsResultOfType('ad');
const isTourActivityAds = createIsResultOfType('tourActivityAds');
const isEntity = createIsResultOfOneOfTypes(['entity', 'entityDisambiguation']);
const isInstantAnswer = createIsResultOfOneOfTypes(
  [
    'computation', 'timeZone', 'translation',
    'weather', 'wordDefinitions', 'myIp',
    'localBusinesses', 'flightBooking',
  ]);
const isWeather = createIsResultOfType('weather');

export const plugins = [
  globalStorePlugin,
  userStorePlugin,
  searchStorePlugin,
  notificationsStorePlugin,
];

export const state = () => ({
  productAds: [],
  adTracking: null,
  error: null,
  fetchingResults: false,
  fetchError: false,
  mainlineResults: [],
  mainlineAdResultsCount: 0,
  pagination: null,
  queryCorrection: null,
  relatedQueries: null,
  relatedSearches: null,
  sidebarResults: [],
  spellingSuggestion: null,
});

export const getters = {
  dynamicPagination(st) {
    return st.pagination?.type === PAGINATION_DYNAMIC;
  },
  staticPagination(st) {
    return st.pagination?.type === PAGINATION_STATIC;
  },
  results(st) {
    const {
      mainlineResults,
      pagination,
      queryCorrection,
      relatedQueries,
      relatedSearches,
      sidebarResults,
      spellingSuggestion,
    } = st;
    return {
      mainline: mainlineResults[0] ?? [],
      pagination,
      queryCorrection,
      relatedQueries,
      relatedSearches,
      spellingSuggestion,
      sidebar: sidebarResults ?? [],
    };
  },
  hasMainlineResults: (st) => st.mainlineResults?.length > 0,
  hasProductAdsResults: (st) => st.productAds?.length > 0,
  hasProductAds: (
    { mainlineResults, sidebarResults },
    { hasMainlineResults, hasSidebarResults },
  ) => {
    const hasMainlineProductAds = hasMainlineResults ?
      mainlineResults.some((results) => results.some(isProductAds)) : false;
    const hasSidebarProductAds = hasSidebarResults ?
      sidebarResults.some(isProductAds) : false;
    return hasMainlineProductAds || hasSidebarProductAds;
  },
  hasTextAds: (
    { mainlineResults, sidebarResults },
    { hasMainlineResults, hasSidebarResults },
  ) => {
    const hasMainlineTextAds = hasMainlineResults ?
      mainlineResults.some((results) => results.some(isAd)) : false;
    const hasSidebarTextAds = hasSidebarResults ?
      sidebarResults.some(isAd) : false;
    return hasMainlineTextAds || hasSidebarTextAds;
  },
  hasTourActivityAds: (
    { mainlineResults, sidebarResults },
    { hasMainlineResults, hasSidebarResults },
  ) => {
    const hasMainlineTourActivityAds = hasMainlineResults ?
      mainlineResults.some((results) => results.some(isTourActivityAds)) : false;
    const hasSidebarTourActivityAds = hasSidebarResults ?
      sidebarResults.some(isTourActivityAds) : false;
    return hasMainlineTourActivityAds || hasSidebarTourActivityAds;
  },
  hasEntities: (
    { mainlineResults, sidebarResults },
    { hasMainlineResults, hasSidebarResults },
  ) => {
    const hasMainlineEntities = hasMainlineResults ?
      mainlineResults.some((results) => results.some(isEntity)) : false;
    const hasSidebarEntities = hasSidebarResults ?
      sidebarResults.some(isEntity) : false;
    return hasMainlineEntities || hasSidebarEntities;
  },
  hasInstantAnswers: ({ mainlineResults }, { hasMainlineResults }) => {
    const hasMainlineIAs = hasMainlineResults ?
      mainlineResults.some((results) => results.some(isInstantAnswer)) : false;
    return hasMainlineIAs;
  },
  hasShoppingLink: (st, { hasProductAds, hasProductAdsResults }, rootStore, rootGetters) => {
    const isShoppingLinkMarket = rootGetters['search/isShoppingLinkMarket'];
    const flagEnabled = rootGetters['unleash/getFeatureToggle'](shoppingBingExperiment.flag)?.value === shoppingBingExperiment.enabled;
    const hasProductAdsOrShoppingIntent = hasProductAds || hasProductAdsResults || rootGetters['search/hasShoppingIntent'];
    return (isShoppingLinkMarket || flagEnabled) && hasProductAdsOrShoppingIntent;
  },
  hasSidebarResults: (st) => st.sidebarResults?.length > 0,
  hasSidebarAds: ({ sidebarResults }, { hasSidebarResults }) => {
    const hasSidebarTextAds = hasSidebarResults ?
      sidebarResults.some(isAd) : false;

    return hasSidebarTextAds;
  },
  hasSidebarProductAds: ({ sidebarResults }, { hasSidebarResults }) => {
    const hasSidebarProductAds = hasSidebarResults ?
      sidebarResults.some(isProductAds) : false;

    return hasSidebarProductAds;
  },
  hasSidebarTourActivityAds: ({ sidebarResults }, { hasSidebarResults }) => {
    const hasSidebarTourActivityAds = hasSidebarResults ?
      sidebarResults.some(isTourActivityAds) : false;

    return hasSidebarTourActivityAds;
  },
  hasSidebarEntities: ({ sidebarResults }, { hasSidebarResults }) => {
    const hasSidebarEntities = hasSidebarResults ?
      sidebarResults.some(isEntity) : false;

    return hasSidebarEntities;
  },
  hasMainlineWeather: ({ mainlineResults }, { hasMainlineResults }) => {
    const hasMainlineWeather = hasMainlineResults ?
      mainlineResults.some((results) => results.some(isWeather)) : false;

    return hasMainlineWeather;
  },
  bingTracking(st) {
    return st.adTracking?.type === 'bingAdTracking' ? st.adTracking : null;
  },
};

export const mutations = {
  addMainlineResults(st, results) {
    st.mainlineResults.push(results);
  },
  setProductAds(st, productAds) {
    st.productAds = productAds;
  },
  setAdTracking(st, adTracking) {
    st.adTracking = adTracking;
  },
  setError(st, error) {
    st.error = error;
  },
  setFetchingResults(st, fetching) {
    st.fetchingResults = fetching;
  },
  setFetchError(st, error) {
    st.fetchError = error;
  },
  setMainlineResults(st, results) {
    st.mainlineResults = results;
  },
  setMainlineAdResultsCount(st, count) {
    st.mainlineAdResultsCount = count;
  },
  setPagination(st, pagination) {
    Object.assign(st, { pagination });
  },
  setQueryCorrection(st, queryCorrection) {
    st.queryCorrection = queryCorrection;
  },
  setRelatedQueries(st, relatedQueries) {
    st.relatedQueries = relatedQueries;
  },
  setRelatedSearches(st, relatedSearches) {
    st.relatedSearches = relatedSearches;
  },
  setSidebarResults(st, results) {
    st.sidebarResults = results;
  },
  setSpellingSuggestion(st, spellingSuggestion) {
    st.spellingSuggestion = spellingSuggestion;
  },
};

export const actions = {
  nuxtServerInit({ commit, dispatch }, context) {
    dispatch('global/nuxtServerInit', context);
    dispatch('user/nuxtServerInit', context);
    dispatch('search/nuxtServerInit', context);
    dispatch('goodall/overrideFromQuery', context.route.query);
    const { req } = context;
    const { results, resultsError } = req;
    if (resultsError) {
      commit('setError', resultsError);
      return;
    }
    if (results) {
      // we need to pass in the experiment data from the request here,
      // because the store itself won't have the data yet at this point
      const unleashExperiments = req?.unleash?.map;
      dispatch('populateResults', { results, unleashExperiments });
    }
  },
  nuxtClientInit({ dispatch }, context) {
    dispatch('user/nuxtClientInit', context);
  },
  populateMainline({ commit }, { results: { mainline, sidebar }, unleashExperiments },
  ) {
    if (!mainline) {
      return;
    }

    const toInject = [
      sidebar?.find(({ type }) => type === 'entity'),
      sidebar?.find(({ type }) => type === 'entityDisambiguation'),
      sidebar?.find(({ type }) => type === 'climatePledgeRating'),
      // D.E.: new format in mainline is currently out of scope,
      //       will be activated after initial experiment (see CS-621)
      // sidebar?.find(({ type }) => type === 'tourActivityAds'),
    ].filter((result) => !!result);

    const results = injectResultsAfterResultTypes(
      mainline,
      toInject,
      ['ad', 'productAds'],
    );

    if (results.length > 0) {
      const productAdsExperimentValue = unleashExperiments[webProductAdsFullwidthExperiment.flag];
      const hasProductAdsFullwidth = productAdsExperimentValue ===
        webProductAdsFullwidthExperiment.enabled;
      const groups = splitProductAdsForABTest(results, hasProductAdsFullwidth);
      commit('setProductAds', groups.productAds);
      commit('setMainlineResults', [groups.results]);
      commit('setMainlineAdResultsCount', groups.results.filter(isAd).length);
    }
  },
  populateSidebar({ commit }, { sidebar }) {
    if (sidebar && sidebar.length > 0) {
      commit('setSidebarResults', sidebar);
    }
  },
  populateResults({ commit, dispatch }, { results, unleashExperiments }) {
    const {
      adTracking,
      pagination,
      queryCorrection,
      relatedQueries,
      relatedSearches,
      spellingSuggestion,
    } = results;
    dispatch('populateMainline', { results, unleashExperiments });
    dispatch('populateSidebar', results);
    commit('setPagination', pagination);
    commit('setQueryCorrection', queryCorrection);
    commit('setSpellingSuggestion', spellingSuggestion);
    commit('setAdTracking', adTracking);
    commit('setRelatedQueries', relatedQueries);
    commit('setRelatedSearches', relatedSearches);
  },
  async fetchNextResults({ commit, state: { fetchingResults } }, url) {
    if (fetchingResults) {
      return;
    }
    commit('setFetchError', false);
    commit('setFetchingResults', true);
    try {
      const response = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
      const { mainline, pagination } = await response.json(); // sidebar
      if (mainline && mainline.length > 0) {
        commit('addMainlineResults', mainline);
      }
      commit('setPagination', pagination);
    } catch (err) {
      commit('setFetchError', true);
    }
    commit('setFetchingResults', false);
  },
  trackPageLoad({ getters: { bingTracking }, rootGetters }) {
    const shouldProxy = rootGetters?.['unleash/getFeatureToggle']?.(privacyProxyMs.flag)?.value === privacyProxyMs.enabled;

    if (bingTracking) {
      trackPageLoad({
        url: bingTracking.pageLoadPingUrl,
        proxyUrl: this.$config.searchFeedbackProxyUrl,
        shouldProxy,
      });
    }
  },
  trackAdClick({ getters: { bingTracking }, rootGetters }, pingSuffix) {
    const shouldProxy = rootGetters?.['unleash/getFeatureToggle']?.(privacyProxyMs.flag)?.value === privacyProxyMs.enabled;

    if (bingTracking && pingSuffix) {
      trackAdClick({
        url: bingTracking.pingUrlBase,
        suffix: pingSuffix,
        proxyUrl: this.$config.searchFeedbackProxyUrl,
        shouldProxy,
      });
    }
  },
};
